// AM Advertising Home — the alerts PANEL's view logic (type chips + severity
// counts + the display cap). PURE + tested.
//
// ⚠️ These narrow the PANEL'S LIST ONLY. The book table's status chips come
// from evaluateAlerts' healthByAccount, which has no type-filter parameter at
// all — the table is ground truth across ALL alert types, and a chip selection
// must never change which accounts read critical/attention there (tested).

import type { AlertType, HomeAlert } from './alerts.js';
import type { AdChannel } from '../../types/advertisingHome.js';

export const ALERT_TYPE_OPTIONS: Array<{ value: AlertType; label: string }> = [
  { value: 'no_spend', label: 'No spend' },
  { value: 'spend_no_leads', label: 'No leads' },
  { value: 'cpl_spike', label: 'CPL' },
  { value: 'lead_drought', label: 'Lead drought' },
  { value: 'pacing', label: 'Pacing' },
];

export const ALL_ALERT_TYPES: readonly AlertType[] = ALERT_TYPE_OPTIONS.map((o) => o.value);

const TYPE_LABEL: Record<AlertType, string> = {
  no_spend: 'No spend',
  spend_no_leads: 'No leads',
  cpl_spike: 'CPL',
  lead_drought: 'Lead drought',
  pacing: 'Pacing',
};

/** The alert-TYPE badge's text — shared by the panel row and the status popover. */
export function alertTypeLabel(type: AlertType): string {
  return TYPE_LABEL[type];
}

/** The panel's list under the type chips. An empty set genuinely shows none. */
export function filterAlertsByType(
  alerts: readonly HomeAlert[],
  types: ReadonlySet<AlertType>,
): HomeAlert[] {
  return alerts.filter((a) => types.has(a.type));
}

/**
 * The platform toggle — a SINGLE choice, so 'all' is a real value rather than
 * an empty set (unlike the type chips, where "none ticked" legitimately shows
 * nothing). Composes with the type chips and the verdict strip's severity
 * filter; like them it narrows the PANEL only.
 */
export type AlertPlatformFilter = 'all' | AdChannel;

export function filterAlertsByPlatform(
  alerts: readonly HomeAlert[],
  platform: AlertPlatformFilter,
): HomeAlert[] {
  if (platform === 'all') return [...alerts];
  return alerts.filter((a) => a.channel === platform);
}

export interface SeverityCounts {
  red: number;
  amber: number;
}

/** What the collapsed header shows — nothing is hidden silently. */
export function severityCounts(alerts: readonly HomeAlert[]): SeverityCounts {
  let red = 0;
  let amber = 0;
  for (const a of alerts) {
    if (a.severity === 'red') red += 1;
    else amber += 1;
  }
  return { red, amber };
}

/** The expanded list's cap — a second guard against page-length blowout. */
export const ALERTS_DISPLAY_CAP = 30;

export function capAlerts(
  alerts: readonly HomeAlert[],
  showAll: boolean,
  cap = ALERTS_DISPLAY_CAP,
): { visible: HomeAlert[]; hidden: number } {
  if (showAll || alerts.length <= cap) return { visible: [...alerts], hidden: 0 };
  return { visible: alerts.slice(0, cap), hidden: alerts.length - cap };
}
